import { apiFetchJson } from '../../apiClient';

// The API serialises with ReferenceHandler.Preserve, so arrays arrive as { $id, $values }
// and objects carry $id. Strip that so components work with plain JSON.
export function clean(value) {
  if (Array.isArray(value)) return value.map(clean).filter(v => v !== undefined);
  if (value && typeof value === 'object') {
    if (Array.isArray(value.$values)) return clean(value.$values);
    if (value.$ref) return undefined;
    const out = {};
    Object.entries(value).forEach(([key, v]) => {
      if (key !== '$id') out[key] = clean(v);
    });
    return out;
  }
  return value;
}

export const apiLanguage = (language) => ((language || 'en').startsWith('no') ? 'no' : 'en');

export function createStrengthApi(instance, language) {
  const lang = apiLanguage(language);
  const get = async (url) => clean(await apiFetchJson(instance, url));
  const send = async (url, method, body) => clean(await apiFetchJson(instance, url, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body)
  }));
  const withLang = (url) => `${url}${url.includes('?') ? '&' : '?'}language=${lang}`;
  const forUser = (userId) => (userId ? `?userId=${encodeURIComponent(userId)}` : '');

  return {
    language: lang,

    exercises: () => get(withLang('/api/strength/exercises')),

    plans: (userId) => get(withLang(`/api/strength/plans${forUser(userId)}`)),
    createPlan: (plan) => send(withLang('/api/strength/plans'), 'POST', plan),
    updatePlan: (id, plan) => send(withLang(`/api/strength/plans/${id}`), 'PUT', plan),
    deletePlan: (id) => send(`/api/strength/plans/${id}`, 'DELETE'),
    copyPlan: (id) => send(withLang(`/api/strength/plans/${id}/copy`), 'POST'),
    generatePlan: (request) => send('/api/strength/ai/plan', 'POST', { ...request, language: lang }),

    activeSession: () => get(withLang('/api/strength/sessions/active')),
    sessions: (userId) => get(withLang(`/api/strength/sessions${forUser(userId)}`)),
    startSession: ({ planId, planDayId, name } = {}) => send(withLang('/api/strength/sessions'), 'POST', {
      planId: planId || null,
      planDayId: planDayId || null,
      name: name || null,
      // getTimezoneOffset is minutes *behind* UTC, so Oslo in summer is -120.
      utcOffsetMinutes: -new Date().getTimezoneOffset(),
      language: lang
    }),
    logSet: (sessionId, set) => send(withLang(`/api/strength/sessions/${sessionId}/sets`), 'POST', set),
    updateSet: (sessionId, setId, set) => send(withLang(`/api/strength/sessions/${sessionId}/sets/${setId}`), 'PUT', set),
    deleteSet: (sessionId, setId) => send(`/api/strength/sessions/${sessionId}/sets/${setId}`, 'DELETE'),
    finishSession: (sessionId, notes, feeling) => send(withLang(`/api/strength/sessions/${sessionId}/finish`), 'POST', { notes: notes || null, feeling: feeling || null }),
    setExerciseNote: (sessionId, exerciseKey, note) =>
      send(withLang(`/api/strength/sessions/${sessionId}/exercises/${encodeURIComponent(exerciseKey)}/note`), 'PUT', { note: note || null }),
    review: (sessionId) => send(withLang(`/api/strength/sessions/${sessionId}/review`), 'POST'),
    briefing: (planDayId) => get(`/api/strength/sessions/briefing${planDayId ? `?planDayId=${planDayId}` : ''}`),
    deleteSession: (sessionId) => send(`/api/strength/sessions/${sessionId}`, 'DELETE'),
    retryStrava: (sessionId) => send(withLang(`/api/strength/sessions/${sessionId}/strava/retry`), 'POST'),
    guidance: (keys, planDayId) => get(withLang(
      `/api/strength/sessions/guidance?keys=${encodeURIComponent(keys.join(','))}${planDayId ? `&planDayId=${planDayId}` : ''}`
    )),

    progress: (userId, weeks) => get(withLang(`/api/strength/progress${forUser(userId)}${weeks ? `${userId ? '&' : '?'}weeks=${weeks}` : ''}`)),
    insights: (userId, days) => get(withLang(`/api/strength/progress/insights?days=${days || 7}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`)),
    profile: () => get('/api/strength/progress/profile'),
    saveProfile: (profile) => send('/api/strength/progress/profile', 'PUT', profile),
    coachingNotes: () => get('/api/strength/progress/notes'),
    addCoachingNote: (text) => send('/api/strength/progress/notes', 'POST', { text }),
    deleteCoachingNote: (id) => send(`/api/strength/progress/notes/${id}`, 'DELETE'),

    stravaStatus: () => get('/api/strava/status'),
    stravaAuthorizeUrl: (redirectUri) => get(`/api/strava/authorize-url?redirectUri=${encodeURIComponent(redirectUri)}`),
    stravaExchange: ({ code, state, scope }) => send('/api/strava/exchange', 'POST', { code, state, scope }),
    stravaDisconnect: () => send('/api/strava/connection', 'DELETE')
  };
}
